import React, { useState } from 'react';
import { AlertBanner, Button, ButtonBar, Checkbox, ConfirmButton, Dialog } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { ProjectFeeOverrideSettings } from '@/services/fees';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { useMergeState } from '@/utils/hooks';

interface ProjectFeeOverrideSettingsDialogProps {
    onClose(): void;
    onAccept(overrideSettings: ProjectFeeOverrideSettings): Promise<any>;
    show: boolean;
}

const ProjectFeeOverrideSettingsDialog: React.FC<ProjectFeeOverrideSettingsDialogProps> = ({ onClose, onAccept, show }) => {
    const [saving, setSaving] = useState(false);
    const [overrideSettings, mergeOverrideSettings, setOverrideSettings] = useMergeState<ProjectFeeOverrideSettings>({});

    const handleOpen = async () => {
        setSaving(false);
        setOverrideSettings({});
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const handleAccept = async () => {
        setSaving(true);

        try {
            await triggerHandler(onAccept, overrideSettings);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const showWarning = overrideSettings.options || overrideSettings.orders || overrideSettings.optionPresets;

    return (
        <Dialog
            open={show}
            onOpenChange={(open) => {
                if (!open) handleCancelClick();
            }}
            onOpen={handleOpen}>
            <Dialog.Content>
                <Dialog.Header>
                    <Trans>Edit fees and VAT</Trans>
                </Dialog.Header>
                <p>
                    <Trans>
                        This change will apply for all future buyer options and orders. Would you also like to apply these
                        changes to the the existing buyer options and orders?
                        <br />
                        <br />
                        <strong>Apply to existing:</strong>
                    </Trans>
                </p>
                <div className="form-group">
                    <Checkbox
                        checked={overrideSettings.options || false}
                        onChange={(e) => mergeOverrideSettings({ options: e.target.checked })}>
                        <Trans>Options</Trans>
                    </Checkbox>

                    <Checkbox
                        checked={overrideSettings.orders || false}
                        onChange={(e) => mergeOverrideSettings({ orders: e.target.checked })}>
                        <Trans>Orders</Trans>
                    </Checkbox>

                    <Checkbox
                        checked={overrideSettings.offers || false}
                        onChange={(e) => mergeOverrideSettings({ offers: e.target.checked })}>
                        <Trans>Offers</Trans>
                    </Checkbox>

                    <Checkbox
                        checked={overrideSettings.changeRequests || false}
                        onChange={(e) => mergeOverrideSettings({ changeRequests: e.target.checked })}>
                        <Trans>Change requests</Trans>
                    </Checkbox>

                    <Checkbox
                        checked={overrideSettings.externalAgreements || false}
                        onChange={(e) => mergeOverrideSettings({ externalAgreements: e.target.checked })}>
                        <Trans>External agreements</Trans>
                    </Checkbox>

                    <Checkbox
                        checked={overrideSettings.optionPresets || false}
                        onChange={(e) => mergeOverrideSettings({ optionPresets: e.target.checked })}>
                        <Trans>Styles</Trans>
                    </Checkbox>
                </div>
                {showWarning && (
                    <AlertBanner type="warning">
                        <Trans>
                            You are changing fees and VAT definitions that buyers may have been exposed to already. Are you
                            sure you would like to proceed? This may even result in different prices for choices your clients
                            have already made and signed on.
                        </Trans>
                    </AlertBanner>
                )}
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={handleCancelClick} disabled={saving}>
                            <Trans>Cancel</Trans>
                        </Button>
                        {showWarning ? (
                            <ConfirmButton
                                dialogTitle={<Trans>Are you sure?</Trans>}
                                dialogBody={<Trans>Are you sure you would like to continue?</Trans>}
                                onConfirm={handleAccept}
                                variant="primary"
                                loading={saving}>
                                <Trans>Save</Trans>
                            </ConfirmButton>
                        ) : (
                            <Button variant="primary" onClick={handleAccept} loading={saving}>
                                <Trans>Save</Trans>
                            </Button>
                        )}
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};

export default ProjectFeeOverrideSettingsDialog;
